import { useCallback } from 'react';
import type { GetEventCTAPageInfoFragment } from '../../components/CtaButtons/CtaButtons.graphql.generated';
import { EventPageType, EventRSVPAttendanceStatus } from '@graphql/generated-base';
import { useTranslation } from '@shared/core';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';

type FilteredPages = {
  pagesHidden: GetEventCTAPageInfoFragment[];
  pagesPasswordProtected: GetEventCTAPageInfoFragment[];
};

type ButtonsVisibility = {
  shouldDisplayUnlockButton: Maybe<boolean>;
  shouldDisplayRSVPButton: boolean;
  // True only when the specificGuests swap is what surfaced the unlock button (no password gate
  // to pass). Consumers use it to open straight into find-invite with the password gate skipped.
  // Stays false whenever a password/blocked-pages branch already showed unlock, so those guests
  // keep their password form.
  isSpecificGuestsOnlyUnlock: boolean;
};

type CustomPageWithSlug = GetEventCTAPageInfoFragment & { page: { pageSlug: string } };

type GetButtonsVisibilityOptions = Readonly<{
  pages: Maybe<GetEventCTAPageInfoFragment>[];
  welcomePage: Maybe<GetEventCTAPageInfoFragment>;
  schedulePage: Maybe<GetEventCTAPageInfoFragment>;
  rsvpIsEnabled: Maybe<boolean>;
  rsvpIsPasswordProtected: Maybe<boolean>;
  hasUnlocked: Maybe<boolean>;
  hasIdentity: Maybe<boolean>;
  hasSpecificGuestsPages: Maybe<boolean>;
  requirePasswordForEventDate: Maybe<boolean>;
  requirePasswordForEventLocation: Maybe<boolean>;
  customPages?: Maybe<Array<CustomPageWithSlug>>;
}>;

const filterPages = (pages?: Maybe<GetEventCTAPageInfoFragment>[]): FilteredPages => {
  const pagesPasswordProtected: GetEventCTAPageInfoFragment[] = [];
  const pagesHidden: GetEventCTAPageInfoFragment[] = [];

  pages?.forEach(page => {
    if (page?.disabled) {
      pagesHidden.push(page);
    } else if (page?.private && page.type !== EventPageType.schedule && page.type !== EventPageType.rsvp) {
      pagesPasswordProtected.push(page);
    }
  });

  return { pagesHidden, pagesPasswordProtected };
};

export const useCtaButtons = () => {
  const { tq } = useTranslation('layoutEngine');
  const { checkIsWelcomePage } = useWelcomePage();
  // overrides rsvp button text via setting in amplitude
  const rsvpCustomLabelFlag = useFeatureValue('rsvpButtonCustomLabel');
  const rsvpButtonCustomLabel = typeof rsvpCustomLabelFlag.payload === 'string' ? rsvpCustomLabelFlag.payload : undefined;

  const getButtonsVisibility = useCallback(
    ({
      pages,
      welcomePage,
      schedulePage,
      rsvpIsEnabled,
      rsvpIsPasswordProtected,
      hasUnlocked,
      hasIdentity,
      hasSpecificGuestsPages,
      requirePasswordForEventDate,
      requirePasswordForEventLocation,
      customPages,
    }: GetButtonsVisibilityOptions): ButtonsVisibility => {
      const customWelcomePage = customPages?.find(pageInfo => checkIsWelcomePage(pageInfo?.page?.pageSlug, pageInfo?.type).isCustomWelcomePage);

      // Use custom welcome page if it exists, otherwise fall back to native welcome page
      const activeWelcomePage = customWelcomePage || welcomePage;

      const filteredPages = filterPages(pages);
      const isUnlocked = hasUnlocked ?? false;
      const isAnyPagePasswordProtected = filteredPages.pagesPasswordProtected.length > 0;
      // Event date/location can be password protected independently of any page being private
      const requiresGuestDetailsPassword = Boolean(requirePasswordForEventDate || requirePasswordForEventLocation);
      const hasBlockedPages = (isAnyPagePasswordProtected || requiresGuestDetailsPassword) && !isUnlocked;
      // specificGuests pages reveal on identification (not password unlock); the guest projection
      // collapses them to hidden, so the only signal is the BE flag + not-yet-identified.
      const hasUnrevealedSpecificGuestPages = Boolean(hasSpecificGuestsPages) && !hasIdentity;
      const rsvpPage = pages.find(page => page?.type === EventPageType.rsvp);
      const isRsvpPageEnabled = !!rsvpPage && !rsvpPage.disabled;

      const result = ((): ButtonsVisibility => {
        // Everything is hidden
        if (filteredPages.pagesHidden.length === pages.length) {
          return { shouldDisplayUnlockButton: false, shouldDisplayRSVPButton: false, isSpecificGuestsOnlyUnlock: false };
        }

        // Welcome page is hidden but RSVP varies. RSVP stays reachable when it is enabled and its
        // page is present and not disabled, mirroring the public-welcome behavior below.
        if (activeWelcomePage?.disabled) {
          if (rsvpIsEnabled && isRsvpPageEnabled) {
            return {
              shouldDisplayUnlockButton: rsvpIsPasswordProtected ? !isUnlocked : hasBlockedPages,
              shouldDisplayRSVPButton: rsvpIsPasswordProtected ? isUnlocked : true,
              isSpecificGuestsOnlyUnlock: false,
            };
          }

          return { shouldDisplayUnlockButton: hasBlockedPages, shouldDisplayRSVPButton: false, isSpecificGuestsOnlyUnlock: false };
        }

        // Welcome page is public and Schedule page is password protected
        if (!schedulePage?.disabled && schedulePage?.private) {
          if (rsvpIsEnabled && isRsvpPageEnabled) {
            return {
              shouldDisplayUnlockButton: !isUnlocked,
              shouldDisplayRSVPButton: rsvpIsPasswordProtected ? isUnlocked : true,
              isSpecificGuestsOnlyUnlock: false,
            };
          }

          return {
            shouldDisplayUnlockButton: !isUnlocked,
            shouldDisplayRSVPButton: false,
            isSpecificGuestsOnlyUnlock: false,
          };
        }

        // Welcome page is public and Schedule page is hidden or public
        if (rsvpIsEnabled && isRsvpPageEnabled) {
          return {
            shouldDisplayUnlockButton: rsvpIsPasswordProtected ? !isUnlocked : hasBlockedPages,
            shouldDisplayRSVPButton: rsvpIsPasswordProtected ? isUnlocked : true,
            isSpecificGuestsOnlyUnlock: false,
          };
        }

        return { shouldDisplayUnlockButton: hasBlockedPages, shouldDisplayRSVPButton: false, isSpecificGuestsOnlyUnlock: false };
      })();

      // No-password specificGuests events surface only RSVP above; add the unlock entry
      // so an unidentified guest has an explicit find-invite path, while preserving the RSVP button
      // when it was independently enabled (fixes mobile where RSVP is excluded from nav).
      // Leaves password branches (which already show unlock) untouched. Only this swap sets
      // isSpecificGuestsOnlyUnlock — the single signal that there is no password gate, so consumers
      // can skip straight to find-invite.
      if (hasUnrevealedSpecificGuestPages && !result.shouldDisplayUnlockButton) {
        return { shouldDisplayUnlockButton: true, shouldDisplayRSVPButton: result.shouldDisplayRSVPButton && isRsvpPageEnabled, isSpecificGuestsOnlyUnlock: true };
      }

      return result;
    },
    [checkIsWelcomePage],
  );

  const getUnlockButtonText = useCallback(
    (hasUnlocked: Maybe<boolean>, unlockLabel: string | undefined = '') => {
      if (hasUnlocked) {
        return tq`Unlock Your Schedule`;
      }

      return unlockLabel || tq`Unlock Guest Details`;
    },
    [tq],
  );

  const getRSVPButtonText = useCallback(
    (rsvpAttendanceStatus: EventRSVPAttendanceStatus | undefined, rsvpLabel: string | undefined) => {
      // Service labels are the untranslated labels coming from the server
      // Reference: https://github.com/joylifeinc/legacy-joy-monorepo/blob/develop/modules/app-server-api/handlers/gqlResolvers/event/EventResolver.js#L720
      const defaultServiceLabels = ['RSVP Here', 'Unlock Website', 'You Are Attending', 'You Are Not Attending'];

      // rsvpLabel might be one of the service labels or the user inputted label
      // If it is a user inputted label, it should be returned as is
      if (rsvpLabel && !defaultServiceLabels.includes(rsvpLabel)) {
        return rsvpLabel;
      }

      // Use these default labels if the attendance status is set
      switch (rsvpAttendanceStatus) {
        case EventRSVPAttendanceStatus.attending:
          return tq`You are attending`;
        case EventRSVPAttendanceStatus.notAttending:
          return tq`You are not attending`;
        case EventRSVPAttendanceStatus.other:
          return tq`Other`;
        case EventRSVPAttendanceStatus.noAttendanceQuestion:
        case EventRSVPAttendanceStatus.unanswered:
        default:
          return tq`Don't Forget to RSVP`;
      }
    },
    [tq],
  );

  return { getButtonsVisibility, getUnlockButtonText, getRSVPButtonText, rsvpButtonCustomLabel };
};
